import type { CustomerOrderTask as OrderTask } from '@einwert/valuation-api-client'
import {
    useFetchNewTaskDocuments,
    useGetAllTaskFilesZipUrl,
    useGetTask,
    useOptimisticPatchTask,
    usePatchTaskDocument,
    useUploadTaskDocuments,
} from 'ClientPortal/src/api/Queries/tasks'
import type { RootUser as User } from '@einwert/valuation-api-client'
import { useGetCurrentUser, useGetUsers } from 'ClientPortal/src/api/Queries/users'
import { taskIsUrgentOrOverdue } from 'ComponentLibrary'
import { compact, isEmpty, isEqual } from 'lodash'
import { Operation } from 'fast-json-patch'
import invariant from 'tiny-invariant'
import { localizeSupportedDocumentTags } from 'ComponentLibrary/src/molecules/SelectTags/constants'
import { Document, DocumentWithIndex } from 'ComponentLibrary/src/molecules/FileUploadModal/types'
import { throwOnForbiddenApiError } from 'ClientPortal/src/api/react-query-config'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

type UseTaskDetailsProps = {
    taskId: string
}

type UseTaskDetailsReturnValue = {
    fileManagement: FileManagementProps
    taskManagement: TaskManagementProps
    users: User[]
    isUsersPending?: boolean
    isUsersInitialLoading?: boolean
    isPending: boolean
    isError: boolean
    currentUser?: User
    isGeneratingFilesZipUrl: boolean
    generateFilesZipUrl: (params: { taskId: string }) => void
    taskData: OrderTask | undefined
}

type TaskManagementProps = {
    isUrgentOrOverdue?: boolean
    isMutatingTask?: boolean
    updateTask: (task: Partial<OrderTask>, onError?: () => void) => void
}

type FileManagementProps = {
    documents: DocumentWithIndex[]
    isDocumentsPending?: boolean
    deleteDocument: (index: number) => void
    downloadDocument: (index: number) => void
    isDownloadingDocument?: boolean
    uploadDocuments: (files: File[]) => void
    updateDocuments: (documents: Document[], onSuccess: () => void) => void
    failedDocumentUploads: string[]
    isUploadingDocuments: boolean
    isUpdatingDocuments: boolean
}

export function useTaskDetails({ taskId }: UseTaskDetailsProps): UseTaskDetailsReturnValue {
    const currentLocale = useCurrentResolvedLocale()
    const { taskData, isTaskPending, isErrorGettingTask } = useGetTask({ taskId, throwOnError: throwOnForbiddenApiError })
    const { users, isUsersPending, isUsersInitialLoading } = useGetUsers({ orderId: taskData?.order_uuid, enabled: !!taskData })
    const { fetchNewTaskDocuments, isFetchingNewTaskDocuments } = useFetchNewTaskDocuments({ taskId })
    const { mutateTask, isPatchingTask } = useOptimisticPatchTask()
    const { mutateTaskDocument, isPatchingTaskDocument } = usePatchTaskDocument()
    const { user: currentUser } = useGetCurrentUser()

    const { uploadTaskDocuments, failedDocumentUploads, isUploadingTaskDocuments } = useUploadTaskDocuments({
        taskId,
    })

    const downloadFileFromLink = (url: string): void => {
        window.open(url, '_self')
    }

    const { isGeneratingFilesZipUrl, generateFilesZipUrl } = useGetAllTaskFilesZipUrl({
        onSuccess: downloadFileFromLink,
    })

    const deleteTaskDocument = (documentIndex: number) => {
        mutateTaskDocument({
            taskId,
            operations: [
                {
                    op: 'remove',
                    path: `/documents/${documentIndex}`,
                },
            ],
        })
    }

    const downloadDocument = async (index: number) => {
        // Since the signed url for the documents are only valid for a few minutes we need to refetch them first
        const response = await fetchNewTaskDocuments()
        const document = response?.documents?.[index]

        if (document) {
            window.open(document.url, '_self')
        }
    }

    const optimisticallyUpdateTask = (updatedTask: Partial<OrderTask>, onError?: () => void) => {
        invariant(taskData, "taskData can't be undefined when editing a task")
        const operations: (Operation | undefined)[] = Object.entries(updatedTask).map(([key, value]) => {
            if (!isEqual(taskData[key as keyof OrderTask], value)) {
                return {
                    op: 'replace' as const,
                    path: `/${key}`,
                    value,
                }
            }
            return undefined
        })
        !isEmpty(operations) &&
            mutateTask(
                {
                    taskId,
                    operations: compact(operations),
                },
                {
                    onError,
                }
            )
    }

    const updateTaskDocuments = (documents: Document[], onSuccess: () => void) => {
        const transformedDocuments = documents.map((document) => ({
            ...document,
            tags: document.tags?.map((tag) => tag.title) ?? [],
        }))

        mutateTask(
            {
                taskId,
                operations: [
                    {
                        op: 'replace',
                        path: '/documents',
                        value: transformedDocuments,
                    },
                ],
            },
            { onSuccess }
        )
    }

    return {
        taskManagement: {
            isMutatingTask: isPatchingTask,
            isUrgentOrOverdue: taskIsUrgentOrOverdue(taskData),
            updateTask: optimisticallyUpdateTask,
        },
        isPending: isTaskPending,
        taskData,
        isError: isErrorGettingTask,
        fileManagement: {
            documents:
                taskData?.documents.map((doc, index) => ({
                    ...doc,
                    tags: localizeSupportedDocumentTags(doc.tags || [], currentLocale),
                    index,
                })) || [],
            updateDocuments: updateTaskDocuments,
            deleteDocument: deleteTaskDocument,
            uploadDocuments: uploadTaskDocuments,
            isUploadingDocuments: isUploadingTaskDocuments,
            isDocumentsPending: isTaskPending,
            isUpdatingDocuments: isPatchingTask || isPatchingTaskDocument,
            failedDocumentUploads,
            downloadDocument,
            isDownloadingDocument: isFetchingNewTaskDocuments,
        },
        users,
        isUsersPending,
        isUsersInitialLoading,
        currentUser,
        isGeneratingFilesZipUrl,
        generateFilesZipUrl,
    }
}
